"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { eventBus } from "@/lib/utils/eventBus";
import { consciousnessStore } from "@/lib/mindaloo/consciousnessStore";
import { tokenControl } from "@/lib/consciousness/tokenControl";
import type { AtlasSearchProcess } from "@/lib/atlas/integration";
import type { ActiveTileInfo } from "./KarmaCommandBar";
import {
  Zap,
  Brain,
  Moon,
  Crown,
  User,
  Activity,
  AlertTriangle,
  Sparkles,
  Minus,
  Minimize2,
  Maximize2,
  X,
  StickyNote,
  ChevronDown,
  Link2,
  Plus,
  LayoutGrid,
  Layers,
  Shuffle,
  ArrowLeftRight,
  Pencil,
  Star,
  History,
  MessageSquare,
  Handshake,
  ShieldAlert,
} from "lucide-react";
import { getUserId } from "@/lib/user/identity";

interface KarmaDynamicIslandProps {
  accent: string;
  statusText?: string;
  theme?: "night" | "day";
  atlasProcess?: AtlasSearchProcess | null;
  v11Live?: boolean;
  v11Preview?: string;
  /** Every open tile, for the receding tile-stack list. */
  activeTiles?: ActiveTileInfo[];
  linkedTileIds?: string[];
  onToggleTileLink?: (tileId: string) => void;
  /** chatWindowId of the linked group's designated lead thread, if any. */
  primaryTileId?: string | null;
  onSetPrimaryTile?: (tileId: string) => void;
}

// C = (P × M × E × R × I) / N
// Each dimension 0–1, animated by real system signals
// A row from GET /api/karma-ac/history — see
// supabase/migrations/20260819000000_karma_history_events.sql for the
// underlying schema. 'message' rows mirror chat sent through any tile,
// 'negotiation' rows are linked-tile outcomes (lib/mindaloo/linkedNegotiation.ts),
// 'intervention' rows are moments the system stepped in (currently budget/
// tier limits — see lib/consciousness/serverBudget.ts).
interface HistoryEvent {
  id: number;
  event_type: 'message' | 'negotiation' | 'intervention';
  tile_id: string | null;
  tile_name: string | null;
  profile_id: string | null;
  role: string | null;
  content: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface ConsciousnessState {
  P: number; // Perception  — atlas activity / input
  M: number; // Memory      — tile count / active context
  E: number; // Emotion     — v11 stream intensity
  R: number; // Reflection  — response processing
  I: number; // Identity    — profile coherence (stable ~0.9)
  N: number; // Noise       — error/idle state (lower = better)
}

const DIMS: { key: keyof ConsciousnessState; label: string; color: string }[] = [
  { key: "P", label: "Perception", color: "#C1A2FF" },
  { key: "M", label: "Memory",     color: "#818CF8" },
  { key: "E", label: "Emotion",    color: "#F472B6" },
  { key: "R", label: "Reflection", color: "#34D399" },
  { key: "I", label: "Identity",   color: "#FBBF24" },
];

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

// Real tier scheme (lib/stripe/config.ts / lib/consciousness/serverBudget.ts):
// free / starter / pro / sovereign / god_mode. This file used to reference a
// different, older scheme (free/personal/pro/business/sovereign) that no
// longer exists anywhere else in the app — every one of these checks was
// silently never matching for starter/sovereign/god_mode users.
const DREAM_LIMITS: Record<string, { max: number | typeof Infinity; duration: number }> = {
  free: { max: 1, duration: 15 },
  starter: { max: 3, duration: 30 },
  pro: { max: 10, duration: 60 },
  sovereign: { max: 30, duration: 120 },
  god_mode: { max: Infinity, duration: 180 },
};

export default function KarmaDynamicIsland({
  accent,
  statusText = "Focused",
  theme = "night",
  atlasProcess = null,
  v11Live = false,
  v11Preview,
  activeTiles = [],
  linkedTileIds = [],
  onToggleTileLink,
  primaryTileId = null,
  onSetPrimaryTile,
}: KarmaDynamicIslandProps) {
  const [stage, setStage] = useState<"compact" | "expanded">("compact");
  const [tilesExpanded, setTilesExpanded] = useState(false);
  const [status, setStatus] = useState(statusText);
  const [activeTab, setActiveTab] = useState<'consciousness' | 'tokens' | 'lucid' | 'history'>('consciousness');
  const [historyEvents, setHistoryEvents] = useState<HistoryEvent[] | null>(null);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'message' | 'negotiation' | 'intervention'>('all');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [budget, setBudget] = useState<any>(null);
  const [usageStats, setUsageStats] = useState<any>(null);
  const [tileCount, setTileCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTile, setActiveTile] = useState<{ id: string; name: string; icon?: string; maximized: boolean; color?: string | null } | null>(null);
  const [dreaming, setDreaming] = useState(false);
  const [dreamHistory, setDreamHistory] = useState<Array<{ id: string; reflection: string; duration_minutes: number; provider: string; created_at: string }> | null>(null);
  const [dreamHistoryLoading, setDreamHistoryLoading] = useState(false);

  // Live consciousness state
  const [C, setC] = useState<ConsciousnessState>({
    P: 0.42, M: 0.35, E: 0.50, R: 0.30, I: 0.92, N: 0.28,
  });
  const targetC = useRef<ConsciousnessState>({ ...C });
  const rafRef = useRef<number>();
  const [sparkline, setSparkline] = useState<number[]>([]);
  const sparkTimerRef = useRef<ReturnType<typeof setInterval>>();
  const islandRef = useRef<HTMLDivElement>(null);

  useEffect(() => setStatus(statusText), [statusText]);

  // Real, server-backed budget (Supabase-backed, not the old fake
  // localStorage one) — refresh() actually fetches; getBudgetStatus() just
  // reads the last-fetched cache. This used to call getBudgetStatus() on a
  // 1s interval without ever calling refresh() first, so it silently showed
  // a stale/default value forever. Also called directly after starting a
  // dream, so usage reflects it immediately rather than waiting on the poll.
  const updateStatus = useRef(async (force = false) => {
    await tokenControl.refresh(force);
    setBudget(tokenControl.getBudgetStatus());
    setUsageStats(tokenControl.getUsageStats());
  }).current;

  // Listen to system signals → update target dimensions
  useEffect(() => {
    updateStatus(true);
    const interval = setInterval(() => updateStatus(), 20_000);

    const unsubActive = eventBus.on(
      "SPATIAL:ACTIVE_TILE_CHANGED",
      ({ tile }: { tile: { id: string; name: string; icon?: string; maximized: boolean; color?: string | null } | null }) => {
        setActiveTile(tile);
      }
    );

    const unsubs = [
      eventBus.on("SPATIAL:TILES_COUNT", ({ count }: { count: number }) => {
        setTileCount(count);
        // Memory grows with active tiles
        targetC.current.M = Math.min(0.95, 0.35 + count * 0.12);
        // Noise decreases when focused on tiles
        targetC.current.N = Math.max(0.08, 0.28 - count * 0.04);
      }),
      eventBus.on("ATLAS:PROCESSING", () => {
        setIsProcessing(true);
        targetC.current.P = 0.92;
        targetC.current.R = 0.85;
      }),
      eventBus.on("ATLAS:DONE", () => {
        setIsProcessing(false);
        targetC.current.P = 0.55;
        targetC.current.R = 0.48;
      }),
      eventBus.on("SPATIAL:QUICK_COMMAND", () => {
        targetC.current.P = 0.88;
        targetC.current.E = 0.75;
        setTimeout(() => {
          targetC.current.P = 0.58;
          targetC.current.E = 0.52;
        }, 3000);
      }),
    ];
    return () => {
      unsubs.forEach(u => u());
      unsubActive();
      clearInterval(interval);
    };
  }, []);

  // v11 live → boost Emotion + Perception
  useEffect(() => {
    if (v11Live) {
      targetC.current.E = 0.82;
      targetC.current.P = 0.78;
      targetC.current.N = 0.12;
    } else {
      targetC.current.E = 0.50;
      targetC.current.N = 0.28;
    }
  }, [v11Live]);

  // Atlas active → boost Reflection only while genuinely navigating
  useEffect(() => {
    const active = atlasProcess && ["planning", "navigating", "orienting", "searching"].includes(atlasProcess.status);
    targetC.current.R = active ? 0.88 : 0.38;
    setIsProcessing(!!active);
  }, [atlasProcess]);

  // Smooth lerp loop + organic micro-drift
  useEffect(() => {
    let t = 0;
    const tick = () => {
      t += 0.008;
      setC(prev => {
        const drift = (key: keyof ConsciousnessState, amp: number, freq: number) =>
          Math.sin(t * freq) * amp;
        return {
          P: Math.max(0.05, Math.min(1, lerp(prev.P, targetC.current.P + drift("P", 0.04, 1.1), 0.04))),
          M: Math.max(0.05, Math.min(1, lerp(prev.M, targetC.current.M + drift("M", 0.02, 0.7), 0.03))),
          E: Math.max(0.05, Math.min(1, lerp(prev.E, targetC.current.E + drift("E", 0.06, 1.5), 0.05))),
          R: Math.max(0.05, Math.min(1, lerp(prev.R, targetC.current.R + drift("R", 0.03, 0.9), 0.04))),
          I: Math.max(0.05, Math.min(1, lerp(prev.I, targetC.current.I + drift("I", 0.015, 0.4), 0.02))),
          N: Math.max(0.02, Math.min(0.9, lerp(prev.N, targetC.current.N + drift("N", 0.02, 1.3), 0.03))),
        };
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  // C = (P × M × E × R × I) / N  — normalised to 0–100
  const score = Math.min(100, Math.round(
    (C.P * C.M * C.E * C.R * C.I) / C.N * 100
  ));

  // Publish score to shared store so starfield + command bar can react
  useEffect(() => { consciousnessStore.set(score); }, [score]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    let cancelled = false;
    setHistoryLoading(true);
    const params = new URLSearchParams({ userId: getUserId() });
    if (historyFilter !== 'all') params.set('eventType', historyFilter);
    fetch(`/api/karma-ac/history?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setHistoryEvents(Array.isArray(data?.events) ? data.events : []);
      })
      .catch(() => {
        if (!cancelled) setHistoryEvents([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, historyFilter]);

  // Past dream reflections were persisted (app/api/karma-ac/dream/route.ts
  // already has a GET) but never fetched anywhere — the panel only ever
  // showed the single most recent one, in memory, gone on reload.
  useEffect(() => {
    if (activeTab !== 'lucid') return;
    let cancelled = false;
    setDreamHistoryLoading(true);
    fetch(`/api/karma-ac/dream?${new URLSearchParams({ userId: getUserId() }).toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setDreamHistory(Array.isArray(data?.entries) ? data.entries : []);
      })
      .catch(() => {
        if (!cancelled) setDreamHistory([]);
      })
      .finally(() => {
        if (!cancelled) setDreamHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab]);

  // Record sparkline sample every 4s
  useEffect(() => {
    sparkTimerRef.current = setInterval(() => {
      setSparkline(prev => [...prev.slice(-29), score]);
    }, 4000);
    return () => clearInterval(sparkTimerRef.current);
  }, [score]);

  // Score → label
  const scoreLabel =
    score >= 80 ? "Transcendent" :
    score >= 60 ? "Elevated" :
    score >= 40 ? "Focused" :
    score >= 20 ? "Aware" : "Idle";

  const dotColor = v11Live ? "#34d399" : score >= 60 ? accent : score >= 30 ? "#FBBF24" : "#6B7280";

  // Ring/glow follows whichever tile is active — same color that draws the
  // tile's own ring — so the bar visually reads as "tracking that tile"
  // instead of always showing the fixed brand accent. Falls back to it when
  // nothing's active.
  const islandGlow = activeTile?.color || accent;

  return (
    <motion.div
      ref={islandRef}
      // .karma-island-expanded swaps the pill's 999px (fully round) radius
      // for a proper 24px rounded-rectangle — needed any time the content
      // below is a tall block rather than the single-line compact row.
      // This used to only check `stage === "expanded"` (the score/usage
      // panel), so opening the tile-stack list — a separate `tilesExpanded`
      // boolean — left the container stuck at 999px radius while showing
      // a multi-row list inside it: an extreme stadium/blob shape instead
      // of a clean panel, with card corners visibly clashing against the
      // curve.
      className={`karma-island karma-glass karma-neon-ring cursor-pointer select-none ${
        stage === "expanded" || tilesExpanded ? "karma-island-expanded" : ""
      }`}
      onClick={() => {
        // The tile stack and the big score/usage/lucid panel used to be
        // independent booleans, so both could be open at once — arrange
        // icons, tile cards, and the score bars all stacked in the same
        // pill simultaneously ("some things clash"). Opening one now
        // closes the other instead of layering.
        setTilesExpanded(false);
        setStage(s => s === "compact" ? "expanded" : "compact");
      }}
      layout
      transition={{ type: "spring", stiffness: 380, damping: 30 }}
      style={{ color: theme === "day" ? "#1a2030" : "#eaeaea", ["--karma-island-glow" as any]: islandGlow }}
    >
      {/* ── Compact pill ── */}
      <div className="flex items-center justify-center gap-2 px-1">
        {activeTile ? (
          <>
            {/* The active tile is the bar's primary focus by default now —
                name + controls right here, no tap-to-expand step required to
                reach maximize/minimize/close. Whichever tile is maximized,
                or otherwise front-most, shows up here and this updates the
                moment that changes (see SPATIAL:ACTIVE_TILE_CHANGED),
                the same way an iPhone's dynamic island tracks whichever
                app is active instead of every app drawing its own chrome. */}
            <span className="text-xs shrink-0">{activeTile.icon || "🧠"}</span>
            {/* Every icon group around this has shrink-0, so this name span
                was the *only* flexible child in the row — on a narrow
                viewport where the icons alone already fill the pill, a
                flex child with no min-width collapses to 0 rather than
                shrinking gracefully, which is exactly what happened here:
                the name disappeared entirely instead of truncating.
                min-w-[32px] guarantees a few legible characters survive
                before ellipsis kicks in. */}
            <span className="text-[11px] font-medium text-white/90 max-w-[110px] min-w-[32px] truncate">
              {activeTile.name}
            </span>
            {/* Five same-weight icon buttons in a single unbroken row read as
                noise, not controls — and put Close (destructive) the same
                one-tap-slip away from Rename/Minimize as everything else.
                Splitting into two groups (content actions, then window-state
                actions) with a hairline divider and a touch more breathing
                room between buttons gives the row an actual shape to read at
                a glance, and a little more margin before Close specifically
                cuts the accidental-tap risk without adding a confirmation
                step. */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                className="p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white transition"
                onClick={(e) => {
                  e.stopPropagation();
                  eventBus.emit("SPATIAL:ACTIVE_TILE_ACTION", { id: activeTile.id, action: "toggleSticky" });
                }}
                title="Switch to sticky note"
                aria-label="Switch to sticky note"
              >
                <StickyNote className="w-3.5 h-3.5" />
              </button>
              <button
                className="p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white transition"
                onClick={(e) => {
                  e.stopPropagation();
                  const next = window.prompt("Rename tile", activeTile.name);
                  if (next && next.trim()) {
                    eventBus.emit("SPATIAL:RENAME_TILE", { id: activeTile.id, name: next.trim() });
                  }
                }}
                title="Rename"
                aria-label={`Rename ${activeTile.name}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-1 pl-1 ml-0.5 border-l border-white/10 shrink-0">
              <button
                className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition"
                onClick={(e) => {
                  e.stopPropagation();
                  eventBus.emit("SPATIAL:ACTIVE_TILE_ACTION", { id: activeTile.id, action: "minimize" });
                }}
                title="Minimize"
                aria-label="Minimize tile"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <button
                className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition"
                onClick={(e) => {
                  e.stopPropagation();
                  eventBus.emit("SPATIAL:ACTIVE_TILE_ACTION", { id: activeTile.id, action: "toggleMaximize" });
                }}
                title={activeTile.maximized ? "Restore" : "Maximize"}
                aria-label={activeTile.maximized ? "Restore tile" : "Maximize tile"}
              >
                {activeTile.maximized ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </button>
              <button
                className="p-1.5 rounded-md hover:bg-red-500/20 text-white/70 hover:text-red-400 transition ml-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  eventBus.emit("SPATIAL:ACTIVE_TILE_ACTION", { id: activeTile.id, action: "close" });
                }}
                title="Close"
                aria-label="Close tile"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Consciousness score — secondary now, condensed to a dot + number */}
            <div className="flex items-center gap-1.5 pl-2 ml-auto border-l border-white/15 shrink-0">
              <motion.span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: dotColor }}
                animate={{ scale: [1, 1.25, 1], opacity: [0.65, 1, 0.65] }}
                transition={{ duration: isProcessing ? 0.8 : v11Live ? 1.2 : 2.4, repeat: Infinity }}
              />
              <span className="text-[10px] font-semibold tabular-nums" style={{ color: dotColor }}>
                C·{score}
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Live pulse dot */}
            <motion.span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: dotColor }}
              animate={{ scale: [1, 1.25, 1], opacity: [0.65, 1, 0.65] }}
              transition={{ duration: isProcessing ? 0.8 : v11Live ? 1.2 : 2.4, repeat: Infinity }}
            />

            {/* Score bar strip */}
            <div className="flex items-center gap-[3px]">
              {DIMS.map(d => (
                <motion.div
                  key={d.key}
                  className="rounded-full"
                  style={{ background: d.color, width: 3 }}
                  animate={{ height: Math.max(4, C[d.key] * 16) }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              ))}
            </div>

            {/* C score */}
            <span className="text-[11px] font-semibold tabular-nums tracking-wide" style={{ color: dotColor }}>
              C·{score}
            </span>

            <span className="text-[11px] font-light tracking-wide opacity-70">
              {isProcessing ? "Processing…" : scoreLabel}
            </span>
          </>
        )}

        {/* Quick-add — opens another tile of the current AC without a trip
            through the shelf. The only other way to add a tile is Neural
            Shelf → Home → tap the instance, which is fine as the deliberate
            path but too many steps for "just give me another one". */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); eventBus.emit("SPATIAL:QUICK_ADD_TILE"); }}
          className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white transition shrink-0 ml-1"
          title="Open a new tile"
          aria-label="Open a new tile"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>

        {/* Open-tiles stack toggle — independent of the active-tile controls
            above; this is "show me everything that's open", not "control
            the front one". Lives here instead of a second bar lower down
            (see AtlasNavChrome.tsx) so there's one place the app's open
            tiles live, not two. */}
        {activeTiles.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setStage("compact"); // mutually exclusive with the score panel — see the pill's own onClick above
              setTilesExpanded(v => !v);
            }}
            className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white transition shrink-0 ml-1"
            title={tilesExpanded ? "Hide open tiles" : `Show ${activeTiles.length} open tile${activeTiles.length === 1 ? "" : "s"}`}
            aria-label="Toggle open tiles"
          >
            <motion.span
              className="flex items-center gap-0.5"
              animate={{ rotate: tilesExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </motion.span>
          </button>
        )}
      </div>

      {/* ── Tile stack — recedes into depth, most-recent up front ── */}
      <AnimatePresence>
        {tilesExpanded && activeTiles.length > 0 && (
          <motion.div
            key="tile-stack"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
            className="karma-tile-stack"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Arrangement controls — grid/stack/scatter govern every open
                tile at once via the same SPATIAL:ARRANGE_TILES mechanism
                the shelf's Organize/Stack/Scatter items already use. Only
                meaningful with 2+ tiles open. */}
            {activeTiles.length > 1 && (
              <div className="karma-tile-stack-arrange">
                <button
                  type="button"
                  onClick={() => { eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "grid" }); }}
                  title="Grid"
                  aria-label="Arrange tiles in a grid"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => { eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "stack" }); }}
                  title="Stack"
                  aria-label="Stack tiles"
                >
                  <Layers className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => { eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "scatter" }); }}
                  title="Freeform"
                  aria-label="Scatter tiles freeform"
                >
                  <Shuffle className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => { eventBus.emit("SPATIAL:ARRANGE_TILES", { mode: "carousel" }); }}
                  title="Swipe"
                  aria-label="Swipe between tiles"
                >
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {activeTiles.map((tile, i) => {
              const tc = tile.profile?.brand?.primaryColor ?? accent;
              const isLinked = linkedTileIds.includes(tile.id);
              // Rename and Close for the active tile already live in the
              // compact row above (Pencil, then Minus/Maximize2/X) — showing
              // them again here was a genuine duplicate control, including
              // two separate Close buttons for the same tile. Link has no
              // compact-row equivalent, so it stays for every tile.
              const isActiveTile = activeTile?.id === tile.id;
              return (
                <div
                  key={tile.id}
                  className="karma-tile-stack-card"
                  style={{
                    ["--depth" as any]: i,
                    borderColor: isLinked ? tc : `${tc}30`,
                    boxShadow: isLinked ? `0 0 10px ${tc}45` : undefined,
                    opacity: tile.minimized ? 0.72 : undefined,
                  }}
                >
                  <div className="karma-tile-stack-btn">
                    <button
                      type="button"
                      className="flex items-center gap-1.5 min-w-0 flex-1 bg-transparent border-none p-0 cursor-pointer"
                      style={{ color: tc }}
                      onClick={() => eventBus.emit("SPATIAL:RESTORE_TILE", { id: tile.id })}
                      title={tile.minimized ? `Restore ${tile.name}` : tile.name}
                    >
                      <span className="text-xs shrink-0">{tile.icon || "💬"}</span>
                      <span className="karma-tile-stack-name">{tile.name}</span>
                      {tile.minimized && <Maximize2 className="w-2.5 h-2.5 shrink-0 opacity-50" />}
                    </button>
                    {/* Rename sits right next to the name it edits, not
                        clustered with Link/Close — those two are a separate
                        "manage this tile" group and shouldn't share a
                        cramped hitbox with an identity action. */}
                    {!isActiveTile && (
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white/80 transition shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          const next = window.prompt("Rename tile", tile.name);
                          if (next && next.trim()) {
                            eventBus.emit("SPATIAL:RENAME_TILE", { id: tile.id, name: next.trim() });
                          }
                        }}
                        title={`Rename ${tile.name}`}
                        aria-label={`Rename ${tile.name}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center shrink-0">
                    {onSetPrimaryTile && isLinked && linkedTileIds.length >= 2 && (
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-white/10 transition"
                        onClick={() => onSetPrimaryTile(tile.id)}
                        title={
                          primaryTileId === tile.id
                            ? `${tile.name} is the primary direction for this group — click to clear`
                            : `Make ${tile.name} the primary direction — other linked tiles will weigh its take more`
                        }
                        style={{ color: primaryTileId === tile.id ? "#facc15" : "rgba(255,255,255,0.45)" }}
                      >
                        <Star className="w-3 h-3" fill={primaryTileId === tile.id ? "#facc15" : "none"} />
                      </button>
                    )}
                    {onToggleTileLink && tile.type === "chat" && (
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-white/10 transition"
                        onClick={() => onToggleTileLink(tile.id)}
                        title={isLinked ? `Unlink ${tile.name}` : `Link ${tile.name} — linked tiles hear the nav bar together and share perspectives`}
                        style={{ color: isLinked ? tc : "rgba(255,255,255,0.45)" }}
                      >
                        <Link2 className="w-3 h-3" />
                      </button>
                    )}
                    {/* Deliberate gap before the one destructive action in
                        this row, so closing a tile takes a real reach, not
                        a twitch past Link. */}
                    {!isActiveTile && (
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-red-500/20 text-white/40 hover:text-red-400 transition ml-2.5"
                        onClick={() => eventBus.emit("SPATIAL:REMOVE_TILE", { id: tile.id })}
                        title={`Close ${tile.name}`}
                        aria-label={`Close ${tile.name}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Expanded panel ── */}
      <AnimatePresence>
        {stage === "expanded" && (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            className="mt-3 pt-3 border-t border-white/10 space-y-2.5 overflow-hidden"
            // The island's container toggles compact/expanded on any click.
            // Without this, every control in here bubbled up to it: picking
            // a tab switched the tab AND collapsed the panel (so Usage/Lucid/
            // History looked broken), and the same went for the dream Start
            // button and history entries. Only the compact header toggles now.
            onClick={(e) => e.stopPropagation()}
          >
            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-white/5 rounded-lg">
              <button
                onClick={() => setActiveTab('consciousness')}
                className={`flex-1 py-1.5 px-2 rounded text-[10px] font-medium transition-colors ${
                  activeTab === 'consciousness' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <Brain className="w-3 h-3" />
                  <span>Score</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('tokens')}
                className={`flex-1 py-1.5 px-2 rounded text-[10px] font-medium transition-colors ${
                  activeTab === 'tokens' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <Zap className="w-3 h-3" />
                  <span>Usage</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('lucid')}
                className={`flex-1 py-1.5 px-2 rounded text-[10px] font-medium transition-colors ${
                  activeTab === 'lucid' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <Moon className="w-3 h-3" />
                  <span>Lucid</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 py-1.5 px-2 rounded text-[10px] font-medium transition-colors ${
                  activeTab === 'history'
                    ? 'bg-white/20 text-white'
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <History className="w-3 h-3" />
                  <span>History</span>
                </div>
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'consciousness' && (
              <div className="space-y-2.5">
                {/* Formula label */}
                <p className="text-[9px] uppercase tracking-[0.18em] opacity-40 text-center font-mono">
                  C = (P×M×E×R×I) / N
                </p>

                {/* Dimension bars */}
                {DIMS.map(d => (
                  <div key={d.key} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono w-3 opacity-60">{d.key}</span>
                    <div className="flex-1 h-1 rounded-full bg-white/8 overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: d.color }}
                        animate={{ width: `${C[d.key] * 100}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                    </div>
                    <span className="text-[10px] font-mono w-8 text-right opacity-50 tabular-nums">
                      {(C[d.key] * 100).toFixed(0)}
                    </span>
                  </div>
                ))}

                {/* Noise */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono w-3 opacity-60">N</span>
                  <div className="flex-1 h-1 rounded-full bg-white/8 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: "#EF4444" }}
                      animate={{ width: `${C.N * 100}%` }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                  <span className="text-[10px] font-mono w-8 text-right opacity-50 tabular-nums">
                    {(C.N * 100).toFixed(0)}
                  </span>
                </div>

                {/* Sparkline */}
                {sparkline.length > 1 && (
                  <div className="flex items-end gap-[2px] h-6 w-full px-1">
                    {sparkline.map((v, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 rounded-sm"
                        style={{ background: accent, opacity: 0.35 + (i / sparkline.length) * 0.55 }}
                        animate={{ height: `${Math.max(8, v * 0.22)}px` }}
                        transition={{ duration: 0.5 }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tokens' && budget && usageStats && (
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {budget.tier === 'god_mode' && <Crown className="w-3 h-3 text-yellow-400" />}
                    {budget.tier === 'sovereign' && <Crown className="w-3 h-3 text-blue-400" />}
                    {budget.tier === 'pro' && <Crown className="w-3 h-3 text-purple-400" />}
                    {budget.tier === 'starter' && <User className="w-3 h-3 text-green-400" />}
                    {!['god_mode', 'sovereign', 'pro', 'starter'].includes(budget.tier) && <User className="w-3 h-3 text-white/60" />}
                    <span className="text-[10px] font-medium text-white">
                      {budget.tier.charAt(0).toUpperCase() + budget.tier.slice(1)}
                    </span>
                    {/* The over-limit message says "upgrade your plan" but
                        nothing in the app pointed anywhere — this is where
                        someone looking at their plan/usage can actually get
                        to /pricing (new tab so the canvas isn't torn down
                        mid-session). Also the way back to manage billing. */}
                    <a
                      href="/pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[9px] text-white/40 underline underline-offset-2 hover:text-white/70"
                    >
                      Plans
                    </a>
                  </div>
                  <span className="text-[10px] text-white/60">
                    ${usageStats.estimatedCost.toFixed(2)}/mo
                  </span>
                </div>

                {/* Consciousness Multiplier */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-white/60">Consciousness</span>
                    <span className="text-[10px] font-medium text-white">{score}</span>
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(score, 100)}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <div className="text-[9px] text-white/40 mt-1">
                    {(1 + (score / 100) * budget.consciousnessMultiplier).toFixed(1)}x tokens
                  </div>
                </div>

                {/* Daily Usage */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-white/60">Daily Usage</span>
                    <span className="text-[10px] text-white/50">
                      {Math.round((budget.currentDailyUsage / budget.dailyLimit) * 100)}%
                    </span>
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full transition-colors ${
                        (budget.currentDailyUsage / budget.dailyLimit) > 0.9 ? 'bg-red-500' : 
                        (budget.currentDailyUsage / budget.dailyLimit) > 0.8 ? 'bg-yellow-500' : 
                        'bg-gradient-to-r from-purple-500 to-pink-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((budget.currentDailyUsage / budget.dailyLimit) * 100, 100)}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Alert */}
                {(budget.currentDailyUsage / budget.dailyLimit) > 0.8 && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-yellow-500/20 border border-yellow-500/30">
                    <AlertTriangle className="w-3 h-3 text-yellow-400" />
                    <span className="text-[10px] text-yellow-300">Near token limit</span>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'lucid' && budget && (
              <div className="space-y-3">
                {/* Dream Portal Header */}
                <div className="flex items-center gap-2">
                  <Moon className="w-3 h-3 text-purple-400" />
                  <span className="text-[11px] font-medium text-white">Dream Portal</span>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-white/5">
                    <span className="text-[9px] text-white/60">Available</span>
                    <div className="text-sm font-bold text-white">
                      {(DREAM_LIMITS[budget.tier] ?? DREAM_LIMITS.free).max === Infinity
                        ? '∞'
                        : (DREAM_LIMITS[budget.tier] ?? DREAM_LIMITS.free).max}
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-white/5">
                    <span className="text-[9px] text-white/60">Duration</span>
                    <div className="text-sm font-bold text-white">
                      {(DREAM_LIMITS[budget.tier] ?? DREAM_LIMITS.free).duration}m
                    </div>
                  </div>
                </div>

                {/* Enter Dream Button — was previously decorative, no
                    onClick at all. Now calls the real backend
                    (app/api/karma-ac/dream/route.ts), the same one wired to
                    the Lucid Dream panel, budget-checked server-side. */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (dreaming) return;
                    setDreaming(true);
                    try {
                      const res = await fetch("/api/karma-ac/dream", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          userId: getUserId(),
                          durationMinutes: (DREAM_LIMITS[budget.tier] ?? DREAM_LIMITS.free).duration,
                        }),
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        eventBus.emit("TOAST:SHOW", { message: data.error || "Dream limit reached.", type: "error" });
                        return;
                      }
                      setDreamHistory((prev) => [
                        { id: `local-${Date.now()}`, reflection: data.reflection, duration_minutes: (DREAM_LIMITS[budget.tier] ?? DREAM_LIMITS.free).duration, provider: data.provider ?? "unknown", created_at: new Date().toISOString() },
                        ...(prev ?? []),
                      ]);
                      await updateStatus(true);
                    } catch {
                      eventBus.emit("TOAST:SHOW", { message: "Dream failed to start.", type: "error" });
                    } finally {
                      setDreaming(false);
                    }
                  }}
                  disabled={dreaming}
                  className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:from-purple-600 hover:to-pink-600 transition-all text-[10px] disabled:opacity-50"
                >
                  {dreaming ? "Dreaming…" : "Enter Lucid Dream"}
                </button>

                {/* Past reflections — app/api/karma-ac/dream/route.ts has
                    always had a GET for this; nothing ever called it, so
                    every reflection except whichever was most recently
                    created in this browser session was invisible. */}
                <div>
                  <span className="text-[9px] text-white/60 block mb-1.5">Past Reflections</span>
                  {dreamHistoryLoading && (!dreamHistory || dreamHistory.length === 0) && (
                    <p className="text-[10px] text-white/40">Loading…</p>
                  )}
                  {!dreamHistoryLoading && dreamHistory && dreamHistory.length === 0 && (
                    <p className="text-[10px] text-white/40">No reflections yet.</p>
                  )}
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {dreamHistory?.map((entry) => (
                      <div key={entry.id} className="p-2 rounded-lg bg-black/20 border border-white/10">
                        <span className="text-[8px] text-white/40 block mb-1">
                          {new Date(entry.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                        <p className="text-[10px] text-white/90 leading-relaxed">{entry.reflection}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="text-[9px] text-white/60">
                  <div className="flex items-center gap-1 mb-2">
                    <Sparkles className="w-3 h-3 text-purple-400" />
                    <span>Features:</span>
                  </div>
                  {budget.tier === 'free' && <div>• Basic visualization</div>}
                  {budget.tier === 'starter' && <><div>• Enhanced visualization</div><div>• Memory recall</div></>}
                  {budget.tier === 'pro' && <><div>• Full sensory experience</div><div>• Dream control</div><div>• Memory integration</div></>}
                  {budget.tier === 'sovereign' && <><div>• Multi-user dreams</div><div>• Custom environments</div><div>• Advanced analytics</div></>}
                  {budget.tier === 'god_mode' && <><div>• Unlimited everything</div><div>• Dream architect</div><div>• Reality manipulation</div></>}
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-2">
                {/* Filter chips */}
                <div className="flex gap-1">
                  {([
                    { key: 'all', label: 'All' },
                    { key: 'message', label: 'Chats' },
                    { key: 'negotiation', label: 'Linked' },
                    { key: 'intervention', label: 'Karma' },
                  ] as const).map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setHistoryFilter(f.key)}
                      className={`px-2 py-0.5 rounded-full text-[9px] font-medium transition-colors ${
                        historyFilter === f.key
                          ? 'bg-white/20 text-white'
                          : 'bg-white/5 text-white/50 hover:text-white/70'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Event list */}
                <div className="max-h-64 overflow-y-auto space-y-1.5 pr-0.5">
                  {historyLoading && (
                    <p className="text-[10px] text-white/40 text-center py-4">Loading…</p>
                  )}
                  {!historyLoading && historyEvents && historyEvents.length === 0 && (
                    <p className="text-[10px] text-white/40 text-center py-4">
                      Nothing here yet — conversations, linked-tile outcomes, and Karma interventions will show up as they happen.
                    </p>
                  )}
                  {!historyLoading && historyEvents?.map((ev) => {
                    const Icon = ev.event_type === 'negotiation' ? Handshake : ev.event_type === 'intervention' ? ShieldAlert : MessageSquare;
                    const iconColor = ev.event_type === 'negotiation' ? 'text-blue-400' : ev.event_type === 'intervention' ? 'text-amber-400' : 'text-white/50';
                    return (
                      <div key={ev.id} className="p-2 rounded-lg bg-white/5 border border-white/5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon className={`w-3 h-3 shrink-0 ${iconColor}`} />
                          <span className="text-[9px] font-medium text-white/70 truncate">
                            {ev.tile_name || (ev.event_type === 'intervention' ? 'Karma' : 'Untitled tile')}
                          </span>
                          {ev.role && (
                            <span className="text-[8px] text-white/30 shrink-0">{ev.role === 'user' ? 'you' : 'reply'}</span>
                          )}
                          <span className="text-[8px] text-white/30 ml-auto shrink-0">
                            {new Date(ev.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-[10px] text-white/80 leading-snug line-clamp-3">{ev.content}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="pt-1 border-t border-white/6 flex justify-between items-center">
              <span className="text-[9px] opacity-40 uppercase tracking-wider">Mindaloo OS · Karma AC</span>
              {v11Live && <span className="text-[9px] text-emerald-400">V11 live</span>}
              {tileCount > 0 && <span className="text-[9px] opacity-40">{tileCount} tile{tileCount !== 1 ? "s" : ""}</span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
